/**
 * Finance position data: accounts receivable and supported accommodation
 * occupancy (current state, not quarter-filtered), plus expenditure by MYOB
 * job (location / program) for a period, which reconciles exactly to the
 * ledger's P&L for the same period via a "Corporate / unallocated" row.
 */
import { Quarter, now, ym, ymd } from '../../clock';
import { rng } from '../../rng';
import { lastSyncedAt } from '../../state';
import { currentFy, fyStartMonth, profitabilityFor, r2 } from './ledger';

// ------------------------------------------------------------ receivables

export interface AccountsReceivable {
    total_outstanding: number;
    open_invoices: number;
    avg_days_outstanding: number;
    aging: { current: number; '31_60': number; '61_90': number; over_90: number };
    top_debtors: { customer: string; outstanding: number }[];
}

/** Funders and customers that owe NPY money. Government names are generic; private ones are invented. */
const DEBTORS: [string, number][] = [
    ['NIAA – Indigenous Advancement Strategy', 0.19],
    ['NT Government – Territory Families, Housing & Communities', 0.12],
    ['SA Health – Aboriginal Health Programs', 0.105],
    ['Department of Social Services (Commonwealth)', 0.09],
    ['NDIA – Agency Managed', 0.085],
    ['Commonwealth Dept of Health – Aged Care Programs', 0.07],
    ['WA Department of Communities', 0.06],
    ['Red Centre Plan Management', 0.045],
    ['Harbourside Contemporary Gallery', 0.032],
    ['Spinifex Plan Partners', 0.028],
    ['Ochre & Wire Gallery', 0.021],
    ['SA Department of Human Services', 0.018],
];

/** AR as at today. Changes gently month to month; identical everywhere within a month. */
export function accountsReceivable(): AccountsReceivable {
    const month = ym(now());
    const r = rng(`finance:ar:${month}`);
    // About three weeks of revenue: NDIS / aged-care claims in arrears plus grant
    // instalments invoiced on milestone acceptance.
    const total = r2(r.around(2960000, 170000, 2640000, 3310000, 2));
    const d31 = r2(total * r.float(0.145, 0.17, 4));
    const d61 = r2(total * r.float(0.07, 0.085, 4));
    const over90 = r2(total * r.float(0.06, 0.075, 4));
    const current = r2(total - d31 - d61 - over90);

    const debtors = DEBTORS.map(([customer, share]) => ({ customer, outstanding: r2(total * share * r.float(0.85, 1.15, 3)) }))
        .sort((a, b) => b.outstanding - a.outstanding)
        .slice(0, 10);

    return {
        total_outstanding: total,
        open_invoices: r.int(206, 248),
        avg_days_outstanding: r.int(28, 34),
        aging: { current, '31_60': d31, '61_90': d61, over_90: over90 },
        top_debtors: debtors,
    };
}

// --------------------------------------------------------- jobs (by_house)

export interface JobRow {
    job_uid: string;
    job_number: string;
    job_name: string;
    parent_name: string;
    revenue: number;
    expenses: number;
    net: number;
    lines_count: number;
}

/** [job #, name, parent, share of FY revenue tagged to the job, expenses as a multiple of its revenue] */
const JOBS: [string, string, string, number, number][] = [
    ['100', 'Head Office – Mparntwe', '', 0.035, 1.85],
    ['110', 'Disability Services – Pukatja (Ernabella)', 'Disability Services (NDIS)', 0.085, 0.86],
    ['111', 'Disability Services – Amata', 'Disability Services (NDIS)', 0.06, 0.91],
    ['112', 'Disability Services – Mutitjulu', 'Disability Services (NDIS)', 0.055, 0.84],
    ['113', 'Disability Services – Warakurna', 'Disability Services (NDIS)', 0.04, 0.97],
    ['120', 'Aged Care – Iwantja (Indulkana)', 'Aged Care', 0.06, 0.93],
    ['121', 'Aged Care – Kaltjiti (Fregon)', 'Aged Care', 0.045, 1.07],
    ['130', 'Ngangkari Program', '', 0.05, 0.88],
    ['140', 'Child Nutrition Program', '', 0.07, 0.92],
    ['150', 'Youth Services – APY Lands', 'Youth Services', 0.06, 0.94],
    ['151', 'Youth Services – Ngaanyatjarra Lands', 'Youth Services', 0.04, 0.96],
    ['160', 'Domestic & Family Violence Service', '', 0.1, 0.95],
    ['170', 'Tjanpi Desert Weavers', '', 0.055, 0.83],
    ['910', 'Kaltukatjara Office Fit-out (legacy)', '', 0, 0],
];

function uuid(seed: string): string {
    const r = rng(`uuid:${seed}`);
    const hex = (n: number) => Array.from({ length: n }, () => r.int(0, 15).toString(16)).join('');
    return `${hex(8)}-${hex(4)}-4${hex(3)}-${'89ab'[r.int(0, 3)]}${hex(3)}-${hex(12)}`;
}

/** Job number of the reconciling row: ledger lines with no job code (interest, donations, untied grants, corporate overheads). */
export const UNALLOCATED_JOB = '';

const cents = (n: number): number => Math.round(n * 100);

/**
 * "Expenditure by Location": MYOB jobs over a period (a quarter, or the
 * current FY to date for null). Job rows hold the share of income and
 * expenditure coded to each job; a final "Corporate / unallocated" row carries
 * the remainder, so the rows sum exactly (to the cent) to profitabilityFor(q)'s
 * revenue_ytd / expenses_ytd / net_profit_ytd for the same period.
 */
export function expenditureByLocation(q: Quarter | null = null): { period: { start: string; end: string }; houses: JobRow[] } {
    const today = now();
    const fy = q ? q.fy : currentFy();
    const pnl = profitabilityFor(q);
    const revenueC = cents(pnl.revenue_ytd);
    const expensesC = cents(pnl.expenses_ytd);
    const r = rng(`finance:jobs:${q ? q.key : `fy${fy}`}`);

    let jobRevC = 0;
    let jobExpC = 0;
    const houses: JobRow[] = JOBS.map(([num, name, parent, share, ratio]) => {
        let rev = Math.round(revenueC * share * r.float(0.94, 1.06, 3));
        let exp = Math.round(rev * ratio * r.float(0.98, 1.02, 3));
        if (share === 0) {
            rev = 0;
            // Legacy job still collecting the odd invoice: a few thousand a quarter.
            exp = q ? cents(r.float(900, 2200, 2)) : cents(r.float(2800, 6400, 2));
        }
        jobRevC += rev;
        jobExpC += exp;
        return {
            job_uid: uuid(`job:${num}`),
            job_number: num,
            job_name: name,
            parent_name: parent,
            revenue: rev / 100,
            expenses: exp / 100,
            net: (rev - exp) / 100,
            lines_count: share === 0 ? r.int(3, 9) : Math.max(4, Math.round(exp / 100 / r.int(3200, 5200))),
        };
    });
    houses.sort((a, b) => Number(a.job_number) - Number(b.job_number));

    const restRev = revenueC - jobRevC;
    const restExp = expensesC - jobExpC;
    houses.push({
        job_uid: uuid('job:unallocated'),
        job_number: UNALLOCATED_JOB,
        job_name: 'Corporate / unallocated',
        parent_name: '',
        revenue: restRev / 100,
        expenses: restExp / 100,
        net: (restRev - restExp) / 100,
        lines_count: Math.max(12, Math.round(restExp / 100 / r.int(2600, 4200))),
    });

    const start = q ? ymd(q.start) : `${fyStartMonth(fy)}-01`;
    const end = q && q.end.getTime() < today.getTime() ? ymd(q.end) : ymd(today);
    return { period: { start, end }, houses };
}

// --------------------------------------------------------------- occupancy

export interface Facility {
    webcare_id: number;
    name: string;
    address: string | null;
    capacity: number | null;
    occupied: number;
    rate: number | null;
}

/** Supported-living and respite houses in community. */
const FACILITIES: [string, string | null, number | null, number][] = [
    ['Pukatja Supported Living House', 'Lot 112, Pukatja Community SA', 4, 4],
    ['Amata Respite House', 'Lot 37, Amata Community SA', 4, 3],
    ['Mutitjulu Supported Living', 'Lot 58, Mutitjulu Community NT', 3, 3],
    ['Iwantja Aged Care Respite', 'Lot 21, Iwantja Community SA', 6, 5],
    ['Warakurna Respite Unit', 'Lot 9, Warakurna Community WA', 3, 1],
    ['Kaltjiti Supported Living', 'Lot 64, Kaltjiti Community SA', 4, 3],
    ['Imanpa Supported Living', 'Lot 15, Imanpa Community NT', 2, 2],
];

export interface OccupancyStats {
    facilities: Facility[];
    totals: { capacity: number; occupied: number; rate: number; residential_facility_count: number; unmatched_zone_areas?: Record<string, number> };
    clients_last_synced_at: string | null;
    facilities_last_synced_at?: string | null;
}

/** GET /api/occupancy/stats. Current state; board drops ids and diagnostics. */
export function occupancyStats(board = false): OccupancyStats {
    const facilities: Facility[] = FACILITIES.map(([name, address, capacity, occupied], i) => ({
        webcare_id: 3011 + i,
        name,
        address,
        capacity,
        occupied,
        rate: capacity ? Math.round((occupied / capacity) * 100) : null,
    })).sort((a, b) => a.name.localeCompare(b.name));

    const residential = facilities.filter(f => f.capacity);
    const capacity = residential.reduce((s, f) => s + (f.capacity ?? 0), 0);
    const occupied = residential.reduce((s, f) => s + f.occupied, 0);
    const totals = {
        capacity,
        occupied,
        rate: capacity > 0 ? Math.round((occupied / capacity) * 100) : 0,
        residential_facility_count: residential.length,
    };

    if (board) {
        return {
            facilities: facilities.map(({ webcare_id, ...f }) => f as Facility),
            totals,
            clients_last_synced_at: lastSyncedAt('clients'),
        };
    }
    return {
        facilities,
        totals: { ...totals, unmatched_zone_areas: {} },
        clients_last_synced_at: lastSyncedAt('clients'),
        facilities_last_synced_at: lastSyncedAt('facilities'),
    };
}
